// 頁首全域搜尋（SPEC #vms-web-220 頂列「全域搜尋」，VMS UX 評審 2026-10-13 P1-13）：⌘K / Ctrl+K 或 / 聚焦。
// 打字即出分組建議，每組最多 5 筆，上下鍵選、Enter 開選到的（沒選就開第一筆）：
//   頁面（選單項目與頁內分頁的名稱、同義詞；只列看得到的）｜設備（名稱、機種、序號末碼；開設備詳細頁，深連結）｜
//   場域與家戶（Enter = 切範圍）｜人（組織 / 家戶管理者；只比對頁面已載入的成員）｜事件（固定一列：在事件中搜尋）
// 不新增後端 API：設備用已載入的「我的設備」、場域用範圍樹與 /v1/me/tree?q=。
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueries, useQuery } from '@tanstack/react-query';
import { isOrgAdmin, orgApi } from '../../lib/planeApi';
import { useScope } from '../../lib/scope';
import { macMatches, modelLabel } from '../../lib/devices';
import { meTreeApi, type MeTreeItem } from '../../lib/orgTreeApi';
import { scopeKeyOf, isRemoteScope } from '../../lib/remoteScope';
import { requestTreeReveal } from '../orgtree/treeReveal';
import { pickRows } from './ScopePicker';
import type { ScopeNode } from '../../lib/myDevices';
import { useHomePath } from '../devices/path';

type T = (k: string, o?: Record<string, unknown>) => string;

export interface PageEntry {
  /** 顯示名（i18n key） */
  labelKey: string;
  /** 上層頁名（i18n key；分頁才有） */
  parentKey?: string;
  path: string;
  /** 同義詞（i18n key，逗號分隔） */
  synKey?: string;
}

/** 頁面候選：看得到的選單項目，加上頁內分頁與常找的功能。visible = 看得到的選單 key。 */
export function pageEntries(visible: Set<string>, extra: { nodeEnroll: boolean; inventory: boolean }): PageEntry[] {
  const out: PageEntry[] = [];
  const add = (key: string, e: PageEntry) => visible.has(key) && out.push(e);
  add('overview', { labelKey: 'nav.overview', path: '/', synKey: 'gsearch.syn_overview' });
  add('live', { labelKey: 'nav.live', path: '/live', synKey: 'gsearch.syn_live' });
  add('playback', { labelKey: 'nav.playback', path: '/playback', synKey: 'gsearch.syn_playback' });
  add('playback', { labelKey: 'sync.title', parentKey: 'nav.playback', path: '/playback/sync' });
  add('playback', { labelKey: 'moment.title', parentKey: 'nav.playback', path: '/playback/moment' });
  add('events', { labelKey: 'nav.events', path: '/events', synKey: 'gsearch.syn_events' });
  add('map', { labelKey: 'nav.map', path: '/map', synKey: 'gsearch.syn_map' });
  add('exports', { labelKey: 'nav.exports', path: '/exports', synKey: 'gsearch.syn_exports' });
  add('exports', { labelKey: 'shares.tab', parentKey: 'nav.exports', path: '/exports/shares', synKey: 'gsearch.syn_shares' });
  add('exports', { labelKey: 'verify.tab', parentKey: 'nav.exports', path: '/exports/verify', synKey: 'gsearch.syn_verify' });
  add('devices', { labelKey: 'nav.devices', path: '/devices', synKey: 'gsearch.syn_devices' });
  add('devices', { labelKey: 'devtabs.firmware', parentKey: 'nav.devices', path: '/devices/firmware', synKey: 'gsearch.syn_firmware' });
  if (extra.nodeEnroll) add('devices', { labelKey: 'nenr.tab', parentKey: 'nav.devices', path: '/devices/inventory/node-enroll', synKey: 'gsearch.syn_enroll' });
  if (extra.inventory || extra.nodeEnroll) add('devices', { labelKey: 'devtabs.inventory', parentKey: 'nav.devices', path: '/devices/inventory', synKey: 'gsearch.syn_inventory' });
  add('sites', { labelKey: 'nav.sites', path: '/sites', synKey: 'gsearch.syn_sites' });
  // 用電（plan SPEC NRG-V1 全域搜尋同義詞）：用電、電費、電價、功率、kWh、度、碳排 → 用電頁；PoE 兩邊都列
  add('energy', { labelKey: 'energy:title', path: '/energy', synKey: 'energy:syn.energy' });
  add('energy', { labelKey: 'energy:tab.ranking', parentKey: 'energy:title', path: '/energy/ranking' });
  add('energy', { labelKey: 'energy:tab.report', parentKey: 'energy:title', path: '/energy/report' });
  add('energy', { labelKey: 'energy:search.leaf_settings', parentKey: 'energy:title', path: '/energy', synKey: 'energy:syn.leaf_settings' });
  // UPS、不斷電、停電、電池供電 → IT 監控 › 網路與應用（篩 UPS；沒開 KMS 的人不出現）
  add('kmgr-network', { labelKey: 'energy:search.ups', parentKey: 'kmgr:nav.group', path: '/kmgr/network?class=ups', synKey: 'energy:syn.ups' });
  add('rules', { labelKey: 'nav.rules', path: '/rules', synKey: 'gsearch.syn_rules' });
  add('ai', { labelKey: 'nav.ai', path: '/ai' });
  add('backup', { labelKey: 'nav.backup', path: '/backup', synKey: 'gsearch.syn_backup' });
  add('people', { labelKey: 'nav.people', path: '/people', synKey: 'gsearch.syn_people' });
  add('people', { labelKey: 'iam.tab_access', parentKey: 'nav.people', path: '/people/access', synKey: 'gsearch.syn_access' });
  add('people', { labelKey: 'iam.tab_external', parentKey: 'nav.people', path: '/people/external', synKey: 'gsearch.syn_external' });
  add('audit', { labelKey: 'nav.audit', path: '/audit', synKey: 'gsearch.syn_audit' });
  add('billing', { labelKey: 'nav.billing', path: '/billing', synKey: 'gsearch.syn_billing' });
  add('billing', { labelKey: 'gsearch.seats', parentKey: 'nav.billing', path: '/billing#seats', synKey: 'gsearch.syn_seats' });
  add('org', { labelKey: 'nav.org', path: '/org', synKey: 'gsearch.syn_org' });
  add('org', { labelKey: 'energy:org.title', parentKey: 'nav.org', path: '/org/energy', synKey: 'energy:syn.org_tariffs' });
  // 我的帳號（使用者選單；每個人都有）
  out.push({ labelKey: 'nav.account', path: '/settings', synKey: 'gsearch.syn_account' });
  out.push({ labelKey: 'apikeys.title', parentKey: 'nav.account', path: '/settings', synKey: 'gsearch.syn_apikeys' });
  out.push({ labelKey: 'settings.notifications', parentKey: 'nav.account', path: '/settings/notifications', synKey: 'gsearch.syn_notify' });
  out.push({ labelKey: 'mine.shared_tab', parentKey: 'nav.account', path: '/settings/shared' });
  out.push({ labelKey: 'localvms.tab', parentKey: 'nav.account', path: '/settings/local-vms' });
  out.push({ labelKey: 'devacct.tab', parentKey: 'nav.account', path: '/settings/developer-account' });
  return out;
}

export function matchPages(t: T, q: string, entries: PageEntry[]): PageEntry[] {
  const s = q.trim().toLowerCase();
  if (!s) return [];
  // 名稱完全相同 > 名稱含有 > 同義詞 / 上層頁名含有（同分照列表順序）
  const score = (e: PageEntry): number => {
    const label = t(e.labelKey).toLowerCase();
    if (label === s) return 0;
    if (label.includes(s)) return 1;
    const syn = [e.parentKey ? t(e.parentKey) : '', e.synKey ? t(e.synKey) : ''].join(',').toLowerCase();
    return syn.includes(s) ? 2 : -1;
  };
  return entries
    .map((e, i) => ({ e, i, sc: score(e) }))
    .filter((x) => x.sc >= 0)
    .sort((a, b) => a.sc - b.sc || a.i - b.i)
    .map((x) => x.e);
}

interface Hit {
  id: string;
  group: 'pages' | 'devices' | 'places' | 'people' | 'events' | 'other';
  label: string;
  sub?: string;
  run(): void;
}

const MAX = 5;

export function GlobalSearch({ visibleNav, extra }: { visibleNav: Set<string>; extra: { nodeEnroll: boolean; inventory: boolean; peopleAdmin: boolean } }) {
  const { t } = useTranslation();
  const nav = useNavigate();
  const { devices, visibleDevices, homes, tree, planes, setScope, org: scopeOrg, orgs } = useScope();
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const wrap = useRef<HTMLDivElement>(null);

  // ⌘K / Ctrl+K / 「/」聚焦（在輸入框裡打 / 不算）
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const typing = e.target instanceof HTMLElement && (e.target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(e.target.tagName));
      if ((e.key === 'k' || e.key === 'K') && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        input.current?.focus();
        setOpen(true);
      } else if (e.key === '/' && !typing) {
        e.preventDefault();
        input.current?.focus();
        setOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  useEffect(() => {
    if (!open) return;
    const off = (e: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', off);
    return () => document.removeEventListener('mousedown', off);
  }, [open]);

  const sq = q.trim();
  const remote = useQuery({
    queryKey: ['me-tree', 'q', sq],
    queryFn: async () => {
      const rs = await Promise.all(planes.map((p) => meTreeApi.tree(p, { q: sq, limit: 50 }).then((r) => r.items)));
      return rs.flat() as MeTreeItem[];
    },
    enabled: open && sq.length >= 2 && planes.length > 0,
    staleTime: 30_000,
    retry: false,
  });

  // 人：這個人是 owner / org_admin 的每個組織的成員，打開搜尋時才載入、快取 5 分鐘（第三輪 R3-P1-7、B3-15：
  // 只比對頁面已載入的那一份時，在 EddyStudio 搜 LivingStyle 的 Spencer 找不到）
  const adminOrgs = useMemo(() => (extra.peopleAdmin ? orgs.filter((o) => isOrgAdmin(o) && o.kind === 'team') : []), [orgs, extra.peopleAdmin]);
  const memberQs = useQueries({
    queries: adminOrgs.map((o) => ({ queryKey: ['org-members', o.org_id], queryFn: () => orgApi.members(o), enabled: open, staleTime: 5 * 60_000, retry: false })),
  });

  const done = () => {
    setOpen(false);
    setQ('');
    input.current?.blur();
  };

  const homePath = useHomePath();
  const hits = useMemo<Hit[]>(() => {
    if (!sq) return [];
    const s = sq.toLowerCase();
    const out: Hit[] = [];
    // 頁面
    for (const e of matchPages(t, sq, pageEntries(visibleNav, extra)).slice(0, MAX))
      out.push({ id: 'p:' + e.path + e.labelKey, group: 'pages', label: t(e.labelKey), sub: e.parentKey ? t(e.parentKey) : undefined, run: () => nav(e.path) });
    // 設備（含通道；不顯示 device_id，序號只比對末碼）
    const homeName = (id: string) => homes.find((h) => h.home_id === id)?.display_name ?? '';
    const devs: Hit[] = [];
    for (const d of devices) {
      // 機種顯示名（me/devices 的 model_name，沒有不帶）優先，搜尋也比對它
      const mn = (x: unknown) => (typeof (x as { model_name?: unknown }).model_name === 'string' ? ((x as { model_name: string }).model_name as string) : '');
      // Kinio Node / Wi-Fi 基地台的 MAC 後 6 碼也比對（SPEC #notify-177）
      const mac = (x: unknown) => (typeof (x as { mac_suffix?: unknown }).mac_suffix === 'string' ? ((x as { mac_suffix: string }).mac_suffix as string) : '');
      const rows = [
        { ref: d.device_ref, name: d.display_name, model: d.model ?? '', mname: mn(d), mac: mac(d) },
        ...(d.children ?? []).map((c) => ({ ref: c.device_ref, name: `${d.display_name} / ${c.display_name ?? ''}`, model: '', mname: mn(c), mac: '' })),
      ];
      for (const r of rows) {
        const tail = r.ref.replace(/[^A-Za-z0-9]/g, '').slice(-6).toLowerCase();
        if (r.name.toLowerCase().includes(s) || r.model.toLowerCase().includes(s) || r.mname.toLowerCase().includes(s) || (s.length >= 3 && tail.endsWith(s)) || macMatches(r.mac, s))
          devs.push({
            id: 'd:' + d.home_id + '/' + r.ref,
            group: 'devices',
            label: r.name,
            // 路徑從組織名開始寫「LivingStyle › lstw › rd」（第六輪 R6-P2-6：原本只寫家戶名「rd」，兩個 rd 分不出來）
            sub: [r.mname || modelLabel(r.model), homePath(d.home_id, homeName(d.home_id))].filter(Boolean).join(' · '),
            run: () => nav(`/devices/${encodeURIComponent(d.home_id)}/${encodeURIComponent(r.ref)}`),
          });
      }
    }
    // 範圍外的設備另成一組「其他範圍」（深連結本來就不受範圍限制，第三輪 R3-P1-7）
    const inScope = new Set(visibleDevices.map((d) => d.home_id));
    // 點了先切到那一戶的範圍再打開（第八輪 B8-06：提示寫「點了會先切範圍」，原本沒切，只在設備頁頂部給按鈕）
    const others: Hit[] = devs
      .filter((h) => !inScope.has(h.id.slice(2).split('/')[0]))
      .map((h) => {
        const hid = h.id.slice(2).split('/')[0];
        const name = homePath(hid, homeName(hid));
        return { ...h, group: 'other' as const, run: () => (setScope('home:' + hid, '', { announce: t('scope.switched_to', { name }) }), h.run()) };
      });
    out.push(...devs.filter((h) => inScope.has(h.id.slice(2).split('/')[0])).slice(0, MAX));
    // 場域與家戶：範圍樹 + 伺服器搜尋的組織樹節點；Enter = 切範圍
    if (sq.length >= 2) {
      const places: Hit[] = [];
      const seen = new Set<string>();
      for (const r of pickRows(t, tree)) {
        if (r.disabled || !(r.name.toLowerCase().includes(s) || r.path.toLowerCase().includes(s))) continue;
        seen.add(r.key);
        // 整個節點都在其他範圍（底下的家戶都不在目前範圍）→ 歸「其他範圍」，計數一起算（第五輪 B5-15）
        const hs = nodeHomes(tree, r.key);
        const outside = hs.length > 0 && !hs.some((h) => inScope.has(h));
        (outside ? others : places).push({ id: 's:' + r.key, group: outside ? 'other' : 'places', label: r.name, sub: r.path, run: () => setScope(r.key) });
      }
      for (const it of remote.data ?? []) {
        if (it.kind === 'device') continue;
        const org = it.path?.find((p) => p.key.startsWith('org:'))?.key.slice(4);
        const key = scopeKeyOf(it, org ?? (it.kind === 'org' ? it.id : undefined));
        if (key === 'all' || seen.has(key)) continue;
        seen.add(key);
        const path = (it.path ?? []).map((p) => p.name).join(' › ');
        const outside = !!scopeOrg && !!org && org !== scopeOrg.org_id;
        (outside ? others : places).push({
          id: 's:' + key,
          group: outside ? 'other' : 'places',
          label: it.name,
          sub: path,
          run: () => {
            setScope(key, isRemoteScope(key) ? [path, it.name].filter(Boolean).join(' › ') : '');
            requestTreeReveal((it.path ?? []).map((p) => p.key));
          },
        });
      }
      out.push(...places.slice(0, MAX));
    }
    // 人：只對管理者；比對我管的每個組織的成員；不在目前範圍的組織 → 「其他範圍」，點了先切範圍再開成員抽屜
    if (extra.peopleAdmin) {
      const seen = new Set<string>();
      const people: Hit[] = [];
      adminOrgs.forEach((o, i) => {
        for (const m of (memberQs[i]?.data ?? []) as { user_id: string; display_name?: string; email?: string }[]) {
          if (seen.has(o.org_id + m.user_id)) continue;
          if (!((m.display_name ?? '').toLowerCase().includes(s) || (m.email ?? '').toLowerCase().includes(s))) continue;
          seen.add(o.org_id + m.user_id);
          const here = !scopeOrg || scopeOrg.org_id === o.org_id;
          const go = () => nav('/people/members?member=' + encodeURIComponent(m.user_id));
          const h: Hit = {
            id: 'u:' + o.org_id + '/' + m.user_id,
            group: here ? 'people' : 'other',
            label: m.display_name || m.email || '',
            sub: [m.display_name ? m.email : '', here ? '' : o.display_name].filter(Boolean).join(' · '),
            run: here ? go : () => (setScope('org:' + o.org_id, '', { announce: t('scope.switched_to', { name: o.display_name }) }), go()),
          };
          (here ? people : others).push(h);
        }
      });
      out.push(...people.slice(0, MAX));
    }
    // 範圍內一筆都沒有、範圍外有 → 先說（B3-15）；說明放在「其他範圍」這一組的第一列，標題只出一次、數字是這一組的全部（第五輪 B5-15）
    if (others.length) {
      const inCount = out.filter((h) => h.group === 'devices' || h.group === 'people').length;
      const rest = out.filter((h) => h.group === 'events');
      const head = out.filter((h) => h.group !== 'events');
      out.length = 0;
      if (!inCount) out.push({ id: 'note:other', group: 'other', label: t('gsearch.only_other', { n: others.length }), run: () => undefined }, ...others.slice(0, MAX), ...head, ...rest);
      else out.push(...head, ...others.slice(0, MAX), ...rest);
    }
    // 事件：固定一列
    if (visibleNav.has('events')) out.push({ id: 'e:' + sq, group: 'events', label: t('gsearch.in_events', { q: sq }), run: () => nav('/events?q=' + encodeURIComponent(sq)) });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sq, devices, visibleDevices, homes, tree, remote.data, visibleNav, extra, t, scopeOrg, adminOrgs, homePath, memberQs.map((q) => q.dataUpdatedAt).join()]);

  useEffect(() => setActive(0), [sq]);

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      done();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setActive((i) => Math.min(i + 1, Math.max(0, hits.length - 1)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const h = hits[active] ?? hits[0];
      if (h) {
        h.run();
        done();
      }
    }
  };

  const mac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);
  let last = '';
  return (
    <div className="gsearch" ref={wrap}>
      <input
        ref={input}
        className="input gsearch-input"
        type="search"
        role="combobox"
        aria-expanded={open && !!sq}
        aria-controls="gsearch-list"
        aria-label={t('gsearch.label')}
        placeholder={t('gsearch.placeholder', { key: mac ? '⌘K' : 'Ctrl+K' })}
        value={q}
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKey}
        data-testid="global-search"
      />
      {open && sq && (
        <ul className="gsearch-list card" id="gsearch-list" role="listbox" aria-label={t('gsearch.label')} data-testid="global-search-list">
          {hits.map((h, i) => {
            const head = h.group !== last ? h.group : '';
            last = h.group;
            return [
              head && (
                <li key={'g-' + h.id} className="gsearch-group" role="presentation">
                  {t('gsearch.g_' + head)}
                </li>
              ),
              <li key={h.id} role="option" aria-selected={i === active} className={'gsearch-item' + (i === active ? ' active' : '')} data-group={h.group}>
                <button
                  type="button"
                  tabIndex={-1}
                  onMouseEnter={() => setActive(i)}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    h.run();
                    done();
                  }}
                >
                  <span>{h.label}</span>
                  {h.sub && <span className="muted small"> {h.sub}</span>}
                </button>
              </li>,
            ];
          })}
          {sq.length >= 2 && remote.isError && (
            <li className="muted small" role="presentation">
              {t('gsearch.places_failed')}
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

/** 範圍樹某個節點底下（含自己）的家戶 id。 */
export function nodeHomes(tree: ScopeNode[], key: string): string[] {
  const i = tree.findIndex((n) => n.key === key);
  if (i < 0) return [];
  const out: string[] = [];
  const d = tree[i].depth;
  for (let j = i; j < tree.length && (j === i || tree[j].depth > d); j++) if (tree[j].kind === 'home') out.push(tree[j].key.slice('home:'.length));
  return out;
}
